<script>
	import { onMount } from 'svelte';
	import posthog from 'posthog-js';
	import { cartTotal } from '$stores/cart';
	import { page } from '$app/stores';
	import { goto } from '$app/navigation';
	import { isAppUser } from '$stores/isAppUser';

	let showModal = false;

	onMount(() => {
		// 1) Don't show on these routes
		const disallowedPaths = ['/cart', '/checkout', '/subscribe'];
		const currentPath = window.location.pathname;
		if (disallowedPaths.some((p) => currentPath.includes(p))) {
			return;
		}

		// 2) If we've already shown this modal in THIS session, do not show again
		if (sessionStorage.getItem('subscriptionModalShown') === 'true') {
			return;
		}

		// 3) Subscribe to cartTotal—but only fire once when it first crosses above 30
		const unsubscribe = cartTotal.subscribe((value) => {
			// Guard: do nothing unless cart goes from ≤30 to >30
			if (value > 10 && showModal === false) {
				showModal = true;

				// Mark in sessionStorage so it won’t show again this session
				sessionStorage.setItem('subscriptionModalShown', 'true');

				// Track the event once
				posthog.capture('subscription_modal_shown', { cart_value: value });
				if (posthog.isFeatureEnabled?.('show_subscription_modal')) {
					// Only capture once—no need to double‐capture
					// posthog.capture('subscription_modal_shown', { cart_value: value });
				}

				// Now that we've shown it, we can unsubscribe so this callback never fires again
				unsubscribe();
			}
		});
	});

	function handleViewSubscriptions() {
		posthog.capture('subscription_modal_cta_clicked');
		goto('/subscribe?utm=submodal_click');
	}

	function handleNoThanks() {
		posthog.capture('subscription_modal_no_thanks_clicked');
		showModal = false;
	}
</script>

{#if showModal && !$isAppUser}
	<!-- Backdrop -->
	<div class="modal-backdrop" aria-hidden="true" on:click={() => (showModal = false)}></div>

	<!-- Modal (Slide-down) -->
	<div
		class="modal-wrapper"
		role="dialog"
		aria-modal="true"
		aria-labelledby="subscriptionModalTitle"
		on:click|stopPropagation
	>
		<div class="modal-content">
			<h2 id="subscriptionModalTitle" class="modal-title">Did you know?</h2>
			<p class="modal-body">
				With a £30 subscription, you can get £60 in points to spend—doubling your order and
				increasing your chance to win!
			</p>
			<div class="modal-actions">
				<button class="btn secondary" on:click={handleNoThanks}>No thanks</button>
				<a href="/subscribe?utm=submodal" class="btn primary" on:click={handleViewSubscriptions}>
					View Subscriptions
				</a>
			</div>
		</div>
	</div>
{/if}

<style lang="scss">
	/* ------------------------------------------
   GENERAL MODAL STYLES
------------------------------------------- */

	/* Backdrop overlay */
	.modal-backdrop {
		position: fixed;
		inset: 0; /* top: 0; right: 0; bottom: 0; left: 0; */
		z-index: 40;
		background-color: rgba(0, 0, 0, 0.4);
	}

	/* Main modal wrapper */
	.modal-wrapper {
		position: fixed;
		top: 50px;
		left: 50%;
		transform: translateX(-50%);
		width: 100%;
		max-width: 500px;
		z-index: 50;

		/* Slide-down effect (start hidden above the viewport) */
		animation: slideDown 0.3s ease forwards;
	}

	/* Inner content styling */
	.modal-content {
		background-color: #fff;
		border-radius: 0 0 8px 8px;
		overflow: hidden;
		box-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
		padding: 1.5rem 1.5rem 1.5rem 1.5rem;
	}

	/* Title and Body */
	.modal-title {
		margin: 0 0 1rem;
		font-size: 1.25rem;
		font-weight: bold;
	}

	.modal-body {
		margin-bottom: 1rem;
	}

	/* Actions container */
	.modal-actions {
		display: flex;
		justify-content: space-between;
		align-items: center;
	}

	/* Buttons */
	.btn {
		padding: 0.5rem 1rem;
		cursor: pointer;
		border: none;

		&.secondary {
			background: transparent;
			color: #555;
			text-decoration: underline;
		}

		&.primary {
			white-space: nowrap;
			color: #fff;
			border-radius: 4px;
			text-decoration: none; /* for <a> */
			// &:hover {
			// 	/* You can style the hover state here */
			// }
		}
	}

	/* Keyframes for the slide-down effect */
	@keyframes slideDown {
		from {
			transform: translate(-50%, -100%);
		}
		to {
			transform: translate(-50%, 0);
		}
	}
</style>
